/**
 * issue #76 Phase 2 sub-F - JobTrackerContext (plan §5.2.F)。
 *
 * jobTracker singleton を React Context として配信する。 mount 時に
 * - setCallbacks で showToast / triggerDriveSave を DI
 * - subscribe で state 変化を React state に反映
 * - bootstrap で localStorage の active entry を register
 *
 * Context value:
 * - activeJobs / recentlyFailedJobs: localStorage と tracker state を同期した最新値
 * - registerJob / unregisterJob: jobTracker の薄いラッパ (FingeringPage 等から submit 時に呼ぶ)
 *
 * 配信のみで FingeringPage の OFF/ON 経路は触らない (sub-G/H/I で結線)。
 */
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  listActiveJobs,
  listRecentlyFailedJobs,
  listNeedsDriveCheckJobs,
  type JobLifecycleEntry,
} from '../utils/jobLifecycleStorage';
import * as jobTracker from '../services/jobTracker';
import { useToast } from '../components/ToastProvider';

export interface JobTrackerContextValue {
  activeJobs: JobLifecycleEntry[];
  recentlyFailedJobs: JobLifecycleEntry[];
  /** issue #95 (#28): 自動保存 ON の expired (Drive 保存済みの可能性・要確認)。 */
  needsDriveCheckJobs: JobLifecycleEntry[];
  registerJob: typeof jobTracker.registerJob;
  unregisterJob: typeof jobTracker.unregisterJob;
  /** issue #85: jobTracker が最後に done 通知した jobId (自動復元のトリガ)。未発火は null。 */
  lastDoneJobId: string | null;
  /** issue #116 FE-1: jobTracker が最後に error 通知した jobId (ErrorModal のトリガ)。未発火は null。 */
  lastErrorJobId: string | null;
}

const JobTrackerContext = createContext<JobTrackerContextValue | null>(null);

function readJobsSnapshot(): {
  activeJobs: JobLifecycleEntry[];
  recentlyFailedJobs: JobLifecycleEntry[];
  needsDriveCheckJobs: JobLifecycleEntry[];
} {
  return {
    activeJobs: listActiveJobs(),
    recentlyFailedJobs: listRecentlyFailedJobs(),
    needsDriveCheckJobs: listNeedsDriveCheckJobs(),
  };
}

export const JobTrackerProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const toast = useToast();
  const showToastRef = useRef(toast.showToast);
  useEffect(() => {
    showToastRef.current = toast.showToast;
  }, [toast.showToast]);

  const [snapshot, setSnapshot] = useState(() => readJobsSnapshot());
  const [lastDoneJobId, setLastDoneJobId] = useState<string | null>(null);
  const [lastErrorJobId, setLastErrorJobId] = useState<string | null>(null);

  useEffect(() => {
    // jobTracker への DI: showToast は (kind, msg) → toast.showToast(msg, {kind}) に変換。
    // triggerDriveSave は Phase 3 sub-H で DriveFooterBar が登録するため、 現段階では no-op。
    jobTracker.setCallbacks({
      showToast: (kind, message) => {
        showToastRef.current(message, { kind });
      },
      triggerDriveSave: () => {
        // Phase 3 で結線。 現段階は意図的な no-op。
      },
      // issue #85: done を React state に橋渡し。 FingeringPage が監視して自動復元する。
      onJobDone: (jobId) => {
        setLastDoneJobId(jobId);
      },
      // issue #116 FE-1: error を React state に橋渡し。 FingeringPage が監視して ErrorModal を出す。
      onJobError: (jobId) => {
        setLastErrorJobId(jobId);
      },
    });

    const unsubscribe = jobTracker.subscribe(() => {
      setSnapshot(readJobsSnapshot());
    });

    jobTracker.bootstrap();
    // bootstrap 後の最新値を反映 (broadcast は registerJob 毎に走るが、 bootstrap 完了後の確定値を一回押し直す)
    setSnapshot(readJobsSnapshot());

    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo<JobTrackerContextValue>(
    () => ({
      activeJobs: snapshot.activeJobs,
      recentlyFailedJobs: snapshot.recentlyFailedJobs,
      needsDriveCheckJobs: snapshot.needsDriveCheckJobs,
      registerJob: jobTracker.registerJob,
      unregisterJob: jobTracker.unregisterJob,
      lastDoneJobId,
      lastErrorJobId,
    }),
    [
      snapshot.activeJobs,
      snapshot.recentlyFailedJobs,
      snapshot.needsDriveCheckJobs,
      lastDoneJobId,
      lastErrorJobId,
    ],
  );

  return (
    <JobTrackerContext.Provider value={value}>
      {children}
    </JobTrackerContext.Provider>
  );
};

export function useJobTracker(): JobTrackerContextValue {
  const ctx = useContext(JobTrackerContext);
  if (!ctx) {
    throw new Error('useJobTracker() must be used within <JobTrackerProvider>');
  }
  return ctx;
}

/**
 * registerJob / unregisterJob の薄い helper (Context value から直接取り出すのと同等)。
 * import 場所を Context にまとめるための再 export。
 */
export const registerJob: typeof jobTracker.registerJob = jobTracker.registerJob;
export const unregisterJob: typeof jobTracker.unregisterJob = jobTracker.unregisterJob;
